import React, { useState, useEffect } from 'react';
import { Download, FileDown, Mail, X } from 'lucide-react';
import { addEmailSubscriber, getUsernameFromProfileId } from '../../../../services/firestoreService';
import useBlockStyles from '../common/useBlockStyles';
import { trackEvent } from '../../../../utils/analytics';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'left',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const FILE_TYPE_BADGES = {
  pdf:  { label: 'PDF',  color: 'bg-red-500/20 text-red-400' },
  pgn:  { label: 'PGN',  color: 'bg-green-500/20 text-green-400' },
  epub: { label: 'EPUB', color: 'bg-blue-500/20 text-blue-400' },
  zip:  { label: 'ZIP',  color: 'bg-yellow-500/20 text-yellow-400' },
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * DownloadsRenderer - Renders a featured download card
 * Mirrors SingleProductRenderer layout exactly.
 */
const DownloadsRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    title,
    description,
    buttonText = 'Download',
    coverImage,
    fileUrl,
    fileName,
    fileSize,
    fileType,
    requireEmail = false,
    layout = 'imageLeft',
  } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const [username, setUsername] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailName, setEmailName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // Fetch username from profileId for analytics (same pattern as SingleProductRenderer)
  useEffect(() => {
    if (!profileId) return;
    getUsernameFromProfileId(profileId).then(u => setUsername(u)).catch(() => {});
  }, [profileId]);

  const badge = FILE_TYPE_BADGES[fileType?.toLowerCase()] || { label: fileType?.toUpperCase() || 'FILE', color: 'bg-gray-500/20 text-gray-400' };
  const sizeText = formatFileSize(fileSize);

  const triggerDownload = () => {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName || 'download';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setDownloadSuccess(true);
    setTimeout(() => setDownloadSuccess(false), 4000);
  };

  const handleDownloadClick = () => {
    if (!fileUrl) return;
    trackEvent('Download Button Clicked', { coach: username, fileName, fileType });
    if (requireEmail) {
      setShowEmailModal(true);
    } else {
      triggerDownload();
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setEmailError('Please enter your email');
      return;
    }

    setSubmitting(true);
    setEmailError('');

    try {
      await addEmailSubscriber(profileId, email.trim(), emailName.trim(), 'download');
      trackEvent('Download Email Submitted', { coach: username, fileName, fileType });
      setShowEmailModal(false);
      setEmail('');
      setEmailName('');
      triggerDownload();
    } catch (error) {
      console.error('Email subscribe error:', error);
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // No file uploaded
  if (!fileUrl) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <FileDown size={48} className="mx-auto mb-4" style={{ color: styles.mutedColor }} />
          <p style={{ color: styles.mutedColor }}>No file uploaded</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Download Card */}
        <div
          className="rounded-xl"
          style={{
            backgroundColor: styles.cardBackground,
            border: `1px solid ${styles.borderColor}`,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 items-center">
            {/* Image column */}
            <div className={`${layout === 'imageLeft' ? 'md:order-1' : 'md:order-2'} flex justify-center p-6`}>
              {coverImage ? (
                <img
                  src={coverImage}
                  alt={title || fileName}
                  className="max-w-full h-auto object-contain rounded-xl"
                  style={{ maxHeight: '400px', maxWidth: '400px' }}
                />
              ) : (
                <div
                  className="w-full h-56 flex items-center justify-center rounded-xl"
                  style={{ backgroundColor: styles.sectionBackground }}
                >
                  <FileDown size={48} style={{ color: styles.mutedColor }} />
                </div>
              )}
            </div>

            {/* Info column */}
            <div className={`${layout === 'imageLeft' ? 'md:order-2' : 'md:order-1'} p-6 sm:p-8 space-y-3`}>
              <h4
                className={`${styles.headingSizeClass} font-bold`}
                style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
              >
                {title || fileName}
              </h4>

              {/* File type badge + size */}
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${badge.color}`}>
                  {badge.label}
                </span>
                {sizeText && (
                  <span className="text-sm" style={{ color: styles.mutedColor }}>
                    {sizeText}
                  </span>
                )}
              </div>

              {description && (
                <p
                  className="text-sm sm:text-base line-clamp-4"
                  style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
                >
                  {description}
                </p>
              )}

              {/* Download CTA */}
              <div className="pt-2 flex flex-column">
                <button
                  onClick={handleDownloadClick}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-base transition-colors hover:opacity-90"
                  style={styles.getButtonStyles()}
                >
                  <Download size={18} />
                  {buttonText}
                </button>
              </div>

              {downloadSuccess && (
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-green-400 animate-fade-in">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Your download has started!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Gate Modal */}
      {showEmailModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowEmailModal(false)}
        >
          <div
            className="rounded-xl p-6 w-full max-w-md space-y-4"
            style={{
              backgroundColor: styles.cardBackground,
              border: `1px solid ${styles.borderColor}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-bold" style={{ color: styles.headingColor }}>
                Enter your email to download
              </h4>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X size={18} style={{ color: styles.mutedColor }} />
              </button>
            </div>

            <p className="text-sm" style={{ color: styles.bodyColor }}>
              Get <strong>{title || fileName}</strong> sent to your inbox and stay updated.
            </p>

            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={emailName}
                onChange={(e) => setEmailName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm focus:outline-none"
                style={{
                  backgroundColor: styles.inputBackground || styles.sectionBackground,
                  color: styles.bodyColor,
                  border: `1px solid ${styles.borderColor}`,
                }}
              />
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                required
                className="w-full px-4 py-2.5 rounded-lg text-sm focus:outline-none"
                style={{
                  backgroundColor: styles.inputBackground || styles.sectionBackground,
                  color: styles.bodyColor,
                  border: `1px solid ${styles.borderColor}`,
                }}
              />

              {emailError && (
                <p className="text-red-400 text-sm">{emailError}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-base transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={styles.getButtonStyles()}
              >
                <Mail size={18} />
                {submitting ? 'Processing...' : buttonText}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadsRenderer;
